import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { AppHeader } from '../components/layout/AppHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Shield, UserCheck, UserX, Users, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const FILTERS = [
  { key: 'all', label: 'All users', icon: Users },
  { key: 'pending', label: 'Pending', icon: Clock },
  { key: 'approved', label: 'Approved', icon: CheckCircle2 },
];

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);

  // Redirect non-admins
  useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { status_filter: filter } : {};
      const res = await adminAPI.listUsers(params);
      setUsers(res.data.users);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprove = async (userId, userName) => {
    setActionLoading(userId);
    try {
      await adminAPI.approveUser(userId);
      toast.success(`${userName} has been approved`);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to approve user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to reject and remove ${userName}?`)) return;
    setActionLoading(userId);
    try {
      await adminAPI.rejectUser(userId);
      toast.success(`${userName} has been rejected`);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to reject user');
    } finally {
      setActionLoading(null);
    }
  };

  const pendingCount = users.filter(u => !u.is_approved).length;

  return (
    <div className="min-h-screen bg-background app-noise">
      <AppHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 relative z-10">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[hsl(38,78%,56%)]/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[hsl(38,78%,45%)]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Admin Panel</h1>
            <p className="text-sm text-muted-foreground">Manage user access and approvals</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6" data-testid="admin-filter-tabs">
          {FILTERS.map((f) => (
            <Button
              key={f.key}
              variant={filter === f.key ? 'default' : 'secondary'}
              size="sm"
              className="rounded-xl gap-1.5"
              onClick={() => { setFilter(f.key); setLoading(true); }}
            >
              <f.icon className="w-3.5 h-3.5" />
              {f.label}
              {f.key === 'pending' && pendingCount > 0 && filter !== 'pending' && (
                <Badge className="ml-1 bg-red-500 text-white border-0 text-[10px] px-1.5 py-0">
                  {pendingCount}
                </Badge>
              )}
            </Button>
          ))}
        </div>

        {/* User list */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-20 rounded-2xl" />
            ))}
          </div>
        ) : users.length === 0 ? (
          <Card className="rounded-2xl">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Users className="w-10 h-10 text-muted-foreground mb-3" />
              <h3 className="font-heading text-lg font-semibold mb-1">
                {filter === 'pending' ? 'No pending approvals' : 'No users found'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {filter === 'pending' ? 'All users have been reviewed' : 'No users match this filter'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {users.map((u) => (
              <Card key={u.id} className="rounded-2xl" data-testid="admin-user-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold
                        ${u.is_approved ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}
                      `}>
                        {u.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{u.name}</span>
                          {u.is_admin && (
                            <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px] px-1.5 py-0">Admin</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Signed up: {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Unknown'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {u.is_approved ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-0 gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Approved
                        </Badge>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            className="rounded-xl gap-1"
                            onClick={() => handleApprove(u.id, u.name)}
                            disabled={actionLoading === u.id}
                            data-testid="admin-approve-button"
                          >
                            <UserCheck className="w-3.5 h-3.5" /> Approve
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            className="rounded-xl gap-1"
                            onClick={() => handleReject(u.id, u.name)}
                            disabled={actionLoading === u.id}
                            data-testid="admin-reject-button"
                          >
                            <UserX className="w-3.5 h-3.5" /> Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
